import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice, createSelector } from "@reduxjs/toolkit";
import {
  ApplicationStatus,
  ApplicationFormSchema,
} from "services/application-types";
import { PortalDisplaySchema } from "utils/application-schema-state";
import { StandardObject } from "utils";

// type ApplicationDocumentStatus = "UPLOADED" | "SUBMITTED" | "DELETED";

interface ReviewFields {
  processComments?: string;
  rejectComments?: string;
  returnComments?: string;
  messageToApplicant?: string;
  fieldsNotAllowedToEdit?: string[];
  fieldsAllowedToEdit?: string[];
  fieldsRequiredToEdit?: string[];
}

interface ApplicationState {
  queryParams: StandardObject;
  quickFilter: {
    status?: ApplicationStatus[];
  };
  combinedFilter?: StandardObject; // combination of queryParams and quickFilter
  totalSize: number;
  formId: string | null;
  itemSchemas: ApplicationFormSchema[];
  portalDisplaySchema: PortalDisplaySchema | null;
  reviewFields: StandardObject<ReviewFields>;
}

const initialState: ApplicationState = {
  queryParams: {},
  quickFilter: {},
  combinedFilter: {},
  totalSize: 0,
  formId: null,
  itemSchemas: [],
  portalDisplaySchema: null,
  reviewFields: {},
};

interface ReviewCommentsPayload {
  applicationId: string;
  comments: string;
}

export interface SetFormPayload {
  formId: string | null;
  itemSchemas: ApplicationFormSchema[];
  portalDisplaySchema: PortalDisplaySchema | null;
}

export const applicationSlice = createSlice({
  name: "application",
  initialState,
  reducers: {
    setQueryParams: (state, action: PayloadAction<StandardObject>) => {
      state.queryParams = action.payload;
      if (!!action.payload.status) {
        state.quickFilter = {};
      }
      state.combinedFilter = {
        ...state.queryParams,
        ...state.quickFilter,
      };
    },
    setQuickFilter: (state, action: PayloadAction<StandardObject>) => {
      state.quickFilter = action.payload;
      if (!!action.payload.status) {
        if (!!state.queryParams.status) {
          const oldQueryParams = state.queryParams;
          delete oldQueryParams["status"];
          state.queryParams = oldQueryParams;
        }
      }
      state.combinedFilter = {
        ...state.queryParams,
        ...state.quickFilter,
      };
    },
    setTotalSize: (state, action: PayloadAction<number>) => {
      state.totalSize = action.payload;
    },
    setForm: (state, action: PayloadAction<SetFormPayload>) => {
      state.formId = action.payload.formId;
      state.itemSchemas = action.payload.itemSchemas;
      state.portalDisplaySchema = action.payload.portalDisplaySchema;
    },
    setProcessReviewComments: (
      state,
      action: PayloadAction<ReviewCommentsPayload>
    ) => {
      const { applicationId, comments } = action.payload;
      if (!state.reviewFields[applicationId]) {
        state.reviewFields[applicationId] = {};
      }
      state.reviewFields[applicationId]["processComments"] = comments;
    },
    setRejectReviewComments: (
      state,
      action: PayloadAction<ReviewCommentsPayload>
    ) => {
      const { applicationId, comments } = action.payload;
      if (!state.reviewFields[applicationId]) {
        state.reviewFields[applicationId] = {};
      }
      state.reviewFields[applicationId]["rejectComments"] = comments;
    },
    setReturnReviewComments: (
      state,
      action: PayloadAction<ReviewCommentsPayload>
    ) => {
      const { applicationId, comments } = action.payload;
      if (!state.reviewFields[applicationId]) {
        state.reviewFields[applicationId] = {};
      }
      state.reviewFields[applicationId]["returnComments"] = comments;
    },
    setReturnReviewMessageToApplicant: (
      state,
      action: PayloadAction<{ applicationId: string; message: string }>
    ) => {
      const { applicationId, message } = action.payload;
      if (!state.reviewFields[applicationId]) {
        state.reviewFields[applicationId] = {};
      }
      state.reviewFields[applicationId]["messageToApplicant"] = message;
    },
    setReturnReviewFieldsNotAllowedToEdit: (
      state,
      action: PayloadAction<{ applicationId: string; fields: string[] }>
    ) => {
      const { applicationId, fields } = action.payload;
      if (!state.reviewFields[applicationId]) {
        state.reviewFields[applicationId] = {};
      }
      state.reviewFields[applicationId]["fieldsNotAllowedToEdit"] = fields;
      if (state.reviewFields[applicationId]["fieldsAllowedToEdit"]) {
        delete state.reviewFields[applicationId]["fieldsAllowedToEdit"];
      }
      // filter required field
      const oldRequiredFields =
        state.reviewFields[applicationId]["fieldsRequiredToEdit"];
      if (oldRequiredFields) {
        state.reviewFields[applicationId]["fieldsRequiredToEdit"] =
          oldRequiredFields.filter((f) => !fields.includes(f));
      }
    },
    setReturnReviewFieldsAllowedToEdit: (
      state,
      action: PayloadAction<{ applicationId: string; fields: string[] }>
    ) => {
      const { applicationId, fields } = action.payload;
      if (!state.reviewFields[applicationId]) {
        state.reviewFields[applicationId] = {};
      }
      state.reviewFields[applicationId]["fieldsAllowedToEdit"] = fields;
      if (state.reviewFields[applicationId]["fieldsNotAllowedToEdit"]) {
        delete state.reviewFields[applicationId]["fieldsNotAllowedToEdit"];
      }
      // filter required field
      const oldRequiredFields =
        state.reviewFields[applicationId]["fieldsRequiredToEdit"];
      if (oldRequiredFields) {
        state.reviewFields[applicationId]["fieldsRequiredToEdit"] =
          oldRequiredFields.filter((f) => fields.includes(f));
      }
    },
    setReturnReviewFieldsRequiredToEdit: (
      state,
      action: PayloadAction<{ applicationId: string; fields: string[] }>
    ) => {
      const { applicationId, fields } = action.payload;
      if (!state.reviewFields[applicationId]) {
        state.reviewFields[applicationId] = {};
      }
      // check required fields in fieldsAllowedToEdit and not in fieldsNotAllowedToEdit
      let newRequiredFields: string[] = [];
      fields.forEach((f) => {
        if (!!state.reviewFields[applicationId]["fieldsAllowedToEdit"]) {
          if (
            !state.reviewFields[applicationId]["fieldsAllowedToEdit"].includes(
              f
            )
          ) {
            return;
          }
        }
        if (!!state.reviewFields[applicationId]["fieldsNotAllowedToEdit"]) {
          if (
            state.reviewFields[applicationId][
              "fieldsNotAllowedToEdit"
            ].includes(f)
          ) {
            return;
          }
        }
        newRequiredFields.push(f);
      });
      state.reviewFields[applicationId]["fieldsRequiredToEdit"] =
        newRequiredFields;
    },
    clearReviewFields: (state, action: PayloadAction<string>) => {
      const applicationId = action.payload;
      delete state.reviewFields[applicationId];
      // state.reviewFields[applicationId] = {};
    },
  },
});

const selectApplication = (state: { application: ApplicationState }) =>
  state.application;

export const selectApplicationState = createSelector(
  [selectApplication],
  (s: ApplicationState) => {
    return {
      queryParams: s.queryParams,
      quickFilter: s.quickFilter,
      combinedFilter: s.combinedFilter,
      formId: s.formId,
      itemSchemas: s.itemSchemas,
      portalDisplaySchema: s.portalDisplaySchema,
      reviewFields: s.reviewFields,
      totalSize: s.totalSize,
    };
  }
);

export const {
  setQueryParams,
  setQuickFilter,
  setTotalSize,
  setForm,
  setProcessReviewComments,
  setRejectReviewComments,
  setReturnReviewComments,
  setReturnReviewMessageToApplicant,
  setReturnReviewFieldsAllowedToEdit,
  setReturnReviewFieldsNotAllowedToEdit,
  setReturnReviewFieldsRequiredToEdit,
  clearReviewFields,
} = applicationSlice.actions;

export default applicationSlice.reducer;
